import type { Session } from "../../shared/session/session-context";
import type { ReleaseNote } from "./releases";

/**
 * "What's new" (ruling of 17 Oct 2026): which releases a person has not seen. The "last seen release" is one guide record per
 * person on the server (GET/PUT /me/guides, tour id `release-notes`), whose version is the newest release number they have
 * seen. The server never lowers a stored version, so an old browser tab cannot bring back a release that was already closed.
 */
export const RELEASE_RECORD_ID = "release-notes";

export const WHATS_NEW_PATH = "/whats-new";

interface RecordLike {
  tourId: string;
  version: number;
}

/** Published releases only (an unreleased placeholder is never shown), newest first. */
export function publishedReleases(releases: readonly ReleaseNote[]): ReleaseNote[] {
  return releases.filter((release) => release.status === "published").sort((a, b) => b.batch - a.batch);
}

/** The newest release number this person has seen, or undefined when they never closed or opened What's new. */
export function seenRelease(records: readonly RecordLike[] | undefined): number | undefined {
  const versions = (records ?? []).filter((record) => record.tourId === RELEASE_RECORD_ID).map((record) => record.version);
  return versions.length > 0 ? Math.max(...versions) : undefined;
}

/**
 * What to announce, newest first. Someone who has seen nothing yet (a new account, or anyone before the first release with
 * What's new) gets only the latest release, never the whole history; someone who has seen release N gets every release
 * after N.
 */
export function unseenReleases(published: readonly ReleaseNote[], seen: number | undefined): ReleaseNote[] {
  if (seen === undefined) return published.slice(0, 1);
  return published.filter((release) => release.batch > seen);
}

/**
 * What's new is for internal staff only (ruling of 17 Oct 2026): a partner sees no page, no menu or sidebar entry, no dot and
 * no dialog. A partner is a partner-kind account (a partner-scoped session), or anyone whose every project role is Partner.
 * In test mode the session is the effective caller, so viewing as a partner hides it too.
 *
 * Batch 23: an internal account whose only role on a project is Partner cannot see that project, so `/auth/me` leaves it out
 * of `projectRoles`; the server says so in `partnerRolesOnly` (every project role it holds, on any project, is Partner).
 */
export function isPartnerAudience(session: Pick<Session, "isPartner" | "me">): boolean {
  if (session.isPartner) return true;
  if (session.me.partnerRolesOnly === true) return true;
  const roles = session.me.projectRoles.flatMap((project) => project.roles);
  return roles.length > 0 && roles.every((role) => role === "Partner");
}

/**
 * Who may be announced to by themselves: internal staff of the internal app (the portal's external testers never reach it;
 * partners never get it), never an AI agent's account, and never while a SystemAdmin is in role test mode ("View as"), where
 * nothing is recorded for anyone.
 */
export function mayAnnounce(session: Pick<Session, "user" | "testMode" | "isPartner" | "me">): boolean {
  return session.user.isAgent !== true && session.user.userKind !== "external" && session.testMode?.active !== true && !isPartnerAudience(session);
}
